import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import Image from "next/image";
import useSWR from "swr";

import { slugifyProject } from "../../utils/common";
import CreatedBy from "../CreatedBy";
import { UserFormatableTextDisplay } from "../form-fields/UserFormatableText";
import { IonIcon } from "../Icons.js";
import { MemberDetailsCard } from "../members/MemberDetails";
import { OrganizationDetailsCard } from "../organizations/OrganizationDetails";
import DeleteBlogModal from "./DeleteBlogModal";
import useUser from "@/components/useUser";
import PageLoadingMessage from "../PageLoadingMessage";
import PageNotFound from "../../views/PageNotFound";

const BlogDetails = ({ blogId }) => {
  const { user, isLoading } = useUser();
  const router = useRouter();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: blog, error } = useSWR(`/api/blog-posts/${blogId}/`);
  const { data: author } = useSWR(
    () => `/api/users/${blog.created_by_user.id}/`
  );

  const isBlogLoading = !blog && !error;

  if (isBlogLoading) {
    return <PageLoadingMessage />;
  }

  if (!blog?.id) {
    return <PageNotFound />;
  }

  const isCreatedByCurrentUser =
    !isLoading && user?.id === blog.created_by_user?.id;
  const projectPageUrl = `/community-projects/${slugifyProject(blog.project)}`;

  return (
    <div className="container is-max-desktop">
      <div className="columns">
        <div className="column is-one-third">
          {blog.created_by_user.is_organization_user ? (
            <OrganizationDetailsCard
              organization={author || blog.created_by_user}
            />
          ) : (
            <MemberDetailsCard member={author || blog.created_by_user} />
          )}
        </div>

        <div className="column">
          <h1 className="title">{blog.title}</h1>

          <DeleteBlogModal
            blog={blog}
            showModal={showDeleteModal}
            onSuccess={() => router.push(projectPageUrl)}
            onCancel={() => setShowDeleteModal(false)}
          />

          {isCreatedByCurrentUser && (
            <div className="buttons">
              <Link
                href={`/blogs/${blog.id}/edit/`}
                className="button is-primary"
              >
                <span>Edit</span>
                <span className="icon">
                  <IonIcon name="create" size="large" />
                </span>
              </Link>
              <button
                className="button is-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <span>Delete</span>
                <span className="icon">
                  <IonIcon name="trash" size="large" />
                </span>
              </button>
            </div>
          )}

          <div className="block">
            <CreatedBy
              user={blog.created_by_user}
              createdAt={blog.created_at}
            />
            {blog.project && (
              <p>
                {`In: `}
                <Link href={projectPageUrl}>{blog.project.title}</Link>
              </p>
            )}
          </div>

          <UserFormatableTextDisplay value={blog.body} />

          {blog.main_image_link && (
            <figure className="image is-4by3">
              <Image
                src={blog.main_image_link}
                layout="fill"
                alt={blog.title}
              />
            </figure>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
